if (!customElements.get("video-reels")) {
  customElements.define(
    "video-reels",
    class VideoReels extends HTMLElement {
      constructor() {
        super();
        this.activeClass = "active";
        this.swiper = this.querySelector(".wt-slider__container");
        this._isSectionVisible = false;
      }

      connectedCallback() {
        this._sectionObserver = null;
        this.init();
      }

      disconnectedCallback() {
        if (this._sectionObserver) {
          this._sectionObserver.disconnect();
          this._sectionObserver = null;
        }
        if (this.checkInterval) clearInterval(this.checkInterval);
      }

      getPreferredSource(video) {
        const sources = Array.from(
          video.querySelectorAll("source[data-src]"),
        );
        const hlsSource = sources.find((source) =>
          source.type.includes("mpegurl"),
        );
        const mp4Source = sources.find(
          (source) => source.type === "video/mp4",
        );
        const supportsHls = video.canPlayType(
          "application/vnd.apple.mpegurl",
        );

        return supportsHls && hlsSource
          ? hlsSource.dataset.src
          : mp4Source?.dataset.src;
      }

      loadVideo(video) {
        if (!video || video.getAttribute("src")) return;

        const source = this.getPreferredSource(video);
        if (!source) return;

        video.preload = "auto";
        video.src = source;
        video.load();
      }

      unloadVideo(video) {
        if (!video) return;

        video.pause();
        video.muted = true;
        video.removeAttribute("autoplay");
        video.removeAttribute("loop");
        video.autoplay = false;
        video.loop = false;
        video.preload = "none";

        if (video.getAttribute("src")) {
          video.removeAttribute("src");
          video.load();
        }
      }

      observeSection() {
        const sectionObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this._isSectionVisible = entry.isIntersecting;
              const swiperInstance = this.swiper?.swiper;

              if (!swiperInstance) return;

              if (entry.isIntersecting) {
                this.handleSlideChange(swiperInstance);
              } else {
                swiperInstance.slides.forEach((slide) => {
                  this.unloadVideo(slide.querySelector("video"));
                });
              }
            });
          },
          {
            root: null,
            rootMargin: "200px 0px",
            threshold: 0.01,
          },
        );

        sectionObserver.observe(this);
        this._sectionObserver = sectionObserver;
      }

      hardStopAllExceptActive(swiper) {
        const active = swiper.slides[swiper.activeIndex];

        swiper.slides.forEach((slide) => {
          if (slide !== active) {
            this.unloadVideo(slide.querySelector("video"));
          }
        });
      }

      prepareVideo(video) {
        if (!video || video._reelPrepared) return;
        video._reelPrepared = true;
        video.muted = true;
        video.preload = "none";
        video.removeAttribute("autoplay");
        video.removeAttribute("loop");
        video.autoplay = false;
        video.loop = false;
        video.setAttribute("playsinline", "");
        video.setAttribute("webkit-playsinline", "");

        video.addEventListener("ended", () => {
          const activeVideo = this.querySelector(
            ".swiper-slide-active video",
          );

          if (this._isSectionVisible && video === activeVideo) {
            video.currentTime = 0;
            video.play().catch(() => {});
          }
        });
      }

      sanitizeVideosOnce() {
        if (this._sanitized) return;
        this._sanitized = true;

        this.querySelectorAll("video").forEach((video) => {
          this.prepareVideo(video);
          this.unloadVideo(video);
        });
      }

      playVideoInActiveSlide(swiper) {
        if (!this._isSectionVisible) return;

        const activeSlideVideo =
          this.findActiveSlide(swiper)?.querySelector("video");
        if (!activeSlideVideo) return;

        this.prepareVideo(activeSlideVideo);
        this.loadVideo(activeSlideVideo);
        activeSlideVideo.muted = swiper.el.dataset.sound !== "on";

        setTimeout(() => {
          if (this._isSectionVisible) {
            activeSlideVideo.play().catch(() => {});
          }
        }, 100);
      }

      handleSoundToggle(swiper) {
        swiper.slides.forEach((slide) => {
          const button = slide.querySelector(".wt-video__sound-toggle");
          if (!button || button._bound) return;
          button._bound = true;

          button.addEventListener(
            "click",
            () => {
              const activeSlide = swiper.slides[swiper.activeIndex];
              const activeVideo = activeSlide?.querySelector("video");
              if (!activeVideo) return;

              this.hardStopAllExceptActive(swiper);
              this.loadVideo(activeVideo);

              const soundOn = swiper.el.dataset.sound === "on";
              swiper.el.dataset.sound = soundOn ? "off" : "on";
              activeVideo.muted = soundOn;
              activeVideo.play().catch(() => {});
            },
            { passive: true },
          );
        });
      }

      findActiveSlide(swiper) {
        return swiper.slides[swiper.activeIndex];
      }

      toggleActiveClass(swiper) {
        const activeSlide = this.findActiveSlide(swiper);

        swiper.slides.forEach((slide) => {
          slide.classList.toggle(this.activeClass, slide === activeSlide);
        });
      }

      handleSlideChange(swiper) {
        this.hardStopAllExceptActive(swiper);
        this.toggleActiveClass(swiper);
        this.playVideoInActiveSlide(swiper);
      }

      addVideoEventHandlers(swiper) {
        this._onSlideChange = () => this.handleSlideChange(swiper);
        swiper.on("slideChange", this._onSlideChange);
        swiper.on("slidesLengthChange", () => this.handleSoundToggle(swiper));
        swiper.on("update", () => this.handleSoundToggle(swiper));
      }

      checkSwiperInitialization() {
        const swiperContainer = this.swiper;
        const mySwiperInstance = swiperContainer?.swiper;

        if (
          swiperContainer?.classList.contains("swiper-initialized") &&
          mySwiperInstance
        ) {
          if (!this.swiper?.dataset.sound) {
            this.swiper.dataset.sound = "off";
          }

          this.sanitizeVideosOnce();
          this.addVideoEventHandlers(mySwiperInstance);
          this.handleSoundToggle(mySwiperInstance);
          this.observeSection();
          clearInterval(this.checkInterval);
        }
      }

      init() {
        this.checkInterval = setInterval(
          this.checkSwiperInitialization.bind(this),
          250,
        );
      }
    },
  );
}
